import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, TrendingUp, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  Minus, Plus, X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG, N2_FRAGMENT_DB,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'N1-2'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'N01', number: 1, category: '정수의 개념', difficulty: 'easy',
    question: '다음 중 정수(Integer)가 아닌 것은?',
    choices: ['-5', '0', '3/2', '+7'],
    answer: 2,
    explanation: [
      '정수: 양의 정수(자연수), 0, 음의 정수로 이루어진 수',
      '-5 → 음의 정수 ✓',
      '0 → 정수 ✓',
      '3/2 = 1.5 → 분수이지만 정수가 아님 ✗',
      '+7 → 양의 정수(자연수) ✓',
    ],
    concept: '정수의 개념', misconceptionCode: 'NM11', learnStepId: 'NT06',
    misconception: '분수(3/2)는 유리수이지만 정수가 아닙니다!'
  },
  {
    id: 'N02', number: 2, category: '양수·음수', difficulty: 'easy',
    question: '"영하 8도"를 수로 나타내면?',
    choices: ['+8', '8', '-8', '|8|'],
    answer: 2,
    explanation: [
      '"영하" = 0°C 아래 = 음수',
      '영하 8도 → -8°C',
      '영상 8도라면 +8 또는 8',
    ],
    concept: '양수·음수', misconceptionCode: 'NM01', learnStepId: 'NT01',
    misconception: '영하는 0보다 낮으므로 음수(-) 표현!'
  },
  {
    id: 'N03', number: 3, category: '절댓값', difficulty: 'easy',
    question: '|-7|의 값은?',
    choices: ['-7', '7', '0', '49'],
    answer: 1,
    explanation: [
      '절댓값 = 수직선에서 0까지의 거리',
      '|-7| = 7 (거리는 항상 양수!)',
      '-7은 0에서 왼쪽으로 7칸 → 거리 7',
    ],
    concept: '절댓값', misconceptionCode: 'NM03', learnStepId: 'NT02',
    misconception: '절댓값은 항상 0 이상! |-7|=-7은 틀린 계산입니다.'
  },
  {
    id: 'N04', number: 4, category: '대소 비교', difficulty: 'easy',
    question: '다음 중 대소 관계가 옳은 것은?',
    choices: ['-9 > -3', '-3 < -9', '0 < -1', '-3 > -9'],
    answer: 3,
    explanation: [
      '수직선에서 오른쪽에 있는 수가 더 크다',
      '← -9 … -3 … 0 →',
      '-3은 -9보다 오른쪽 → -3 > -9 ✓',
      '0은 -1보다 오른쪽 → 0 > -1 (③ 틀림)',
    ],
    concept: '대소 비교', misconceptionCode: 'NM05', learnStepId: 'NT03',
    misconception: '음수끼리 비교 시 절댓값이 작을수록 더 큰 수! -3 > -9'
  },
  {
    id: 'N05', number: 5, category: '대소 비교', difficulty: 'easy',
    question: '-5, +3, -1, 0, +2를 작은 수부터 큰 순서로 나열하면?',
    choices: [
      '-5 < -1 < 0 < +2 < +3',
      '+3 < +2 < 0 < -1 < -5',
      '-1 < -5 < 0 < +2 < +3',
      '-5 < 0 < -1 < +2 < +3',
    ],
    answer: 0,
    explanation: [
      '수직선에서 왼쪽(작음) → 오른쪽(큼) 순서로 나열',
      '← -5, -1, 0, +2, +3 →',
      '-5 < -1 < 0 < +2 < +3 ✓',
    ],
    concept: '대소 비교', learnStepId: 'NT03',
  },
  {
    id: 'N06', number: 6, category: '덧셈', difficulty: 'easy',
    question: '(-8) + (+3)의 값은?',
    choices: ['-11', '+11', '-5', '+5'],
    answer: 2,
    explanation: [
      '다른 부호끼리 덧셈: 절댓값의 차를 구하고 절댓값 큰 쪽 부호',
      '|-8| = 8, |+3| = 3',
      '차: 8 - 3 = 5',
      '-8의 절댓값이 더 크므로 음수 → -5',
    ],
    concept: '정수 덧셈', misconceptionCode: 'NM08', learnStepId: 'NT04',
    misconception: '다른 부호 덧셈: 절댓값 차이 + 절댓값 큰 쪽 부호!'
  },
  {
    id: 'N07', number: 7, category: '뺄셈', difficulty: 'easy',
    question: '(-4) - (-9)의 값은?',
    choices: ['-13', '+13', '+5', '-5'],
    answer: 2,
    explanation: [
      '뺄셈 → 덧셈 변환: 뺄 수의 부호를 반대로!',
      '(-4) - (-9) = (-4) + (+9)',
      '다른 부호: |9| - |4| = 5, +9 절댓값 크므로 양수',
      '→ +5',
    ],
    concept: '정수 뺄셈', misconceptionCode: 'NM07', learnStepId: 'NT04',
    misconception: '뺄셈은 덧셈으로 변환! (-4)-(-9) = (-4)+(+9) = +5'
  },
  {
    id: 'N08', number: 8, category: '덧셈·뺄셈', difficulty: 'medium',
    question: '(-3) + (+7) - (-2)의 값은?',
    choices: ['+2', '-2', '+6', '+8'],
    answer: 2,
    explanation: [
      '뺄셈을 덧셈으로 변환',
      '(-3) + (+7) - (-2) = (-3) + (+7) + (+2)',
      '= -3 + 7 + 2 = +6',
    ],
    concept: '혼합 덧뺄셈', learnStepId: 'NT04',
  },
  {
    id: 'N09', number: 9, category: '곱셈 부호', difficulty: 'easy',
    question: '(-3) × (-4)의 값은?',
    choices: ['-12', '+12', '-7', '+7'],
    answer: 1,
    explanation: [
      '같은 부호끼리 곱셈 → 결과 양수',
      '(-) × (-) = +',
      '3 × 4 = 12 → +12',
    ],
    concept: '정수 곱셈', misconceptionCode: 'NM09', learnStepId: 'NT05',
    misconception: '(-) × (-) = + (같은 부호 → 양수)'
  },
  {
    id: 'N10', number: 10, category: '곱셈 부호', difficulty: 'medium',
    question: '(-2) × (+5) × (-3)의 값은?',
    choices: ['-30', '+30', '-10', '+10'],
    answer: 1,
    explanation: [
      '음수 인자 개수: (-2), (-3) → 2개 (짝수) → 결과 양수',
      '(-2) × (+5) = -10',
      '(-10) × (-3) = +30',
    ],
    concept: '곱셈 부호 규칙', learnStepId: 'NT05',
  },
  {
    id: 'N11', number: 11, category: '나눗셈', difficulty: 'medium',
    question: '(-18) ÷ (+6)의 값은?',
    choices: ['+3', '-3', '+12', '-24'],
    answer: 1,
    explanation: [
      '다른 부호끼리 나눗셈 → 결과 음수',
      '절댓값: 18 ÷ 6 = 3',
      '부호: 다른 부호 → -',
      '→ -3',
    ],
    concept: '정수 나눗셈', learnStepId: 'NT05',
  },
  {
    id: 'N12', number: 12, category: '유리수 나눗셈', difficulty: 'medium',
    question: '(-3/4) ÷ (3/8)의 값은?',
    choices: ['-2', '+2', '-9/32', '+9/32'],
    answer: 0,
    explanation: [
      '나눗셈 → 역수 곱셈: (-3/4) × (8/3)',
      '= -(3×8)/(4×3) = -24/12 = -2',
      '부호: 다른 부호 → 음수',
      '→ -2',
    ],
    concept: '유리수 나눗셈', misconceptionCode: 'NM10', learnStepId: 'NT05',
    misconception: '나눗셈은 역수를 곱해야 합니다! ÷(3/8) = ×(8/3)'
  },
  {
    id: 'N13', number: 13, category: '유리수 분류', difficulty: 'medium',
    question: '다음 중 옳지 않은 것은?',
    choices: [
      '모든 자연수는 정수이다',
      '모든 정수는 유리수이다',
      '모든 유리수는 정수이다',
      '0은 유리수이다',
    ],
    answer: 2,
    explanation: [
      '자연수 ⊂ 정수 ⊂ 유리수 (포함 관계)',
      '① 자연수는 정수: ✓',
      '② 정수는 유리수: ✓',
      '③ 유리수는 정수: ✗ (반례: 1/2은 유리수이지만 정수 아님)',
      '④ 0은 유리수: ✓ (0 = 0/1)',
    ],
    concept: '유리수 분류', misconceptionCode: 'NM12', learnStepId: 'NT06',
    misconception: '유리수 > 정수 > 자연수 포함 관계. 유리수 중 정수가 아닌 것도 있음!'
  },
  {
    id: 'N14', number: 14, category: '절댓값', difficulty: 'medium',
    question: '절댓값이 5인 수를 모두 더하면?',
    choices: ['5', '-5', '0', '10'],
    answer: 2,
    explanation: [
      '절댓값이 5인 수: +5와 -5',
      '두 수를 더하면: (+5) + (-5) = 0',
      '부호가 반대이고 절댓값이 같은 두 수의 합은 항상 0!',
    ],
    concept: '절댓값 활용', learnStepId: 'NT02',
  },
  {
    id: 'N15', number: 15, category: '혼합 계산', difficulty: 'medium',
    question: '5 + (-3) × 2 - (-4)의 값은?',
    choices: ['+3', '+7', '-1', '+15'],
    answer: 0,
    explanation: [
      '계산 순서: 곱셈·나눗셈 먼저!',
      '① (-3) × 2 = -6',
      '② 5 + (-6) - (-4)',
      '③ 뺄셈 변환: 5 + (-6) + (+4)',
      '④ = 5 - 6 + 4 = +3',
    ],
    concept: '혼합 계산', misconceptionCode: 'NM13', learnStepId: 'NT07',
    misconception: '혼합 계산: 곱셈·나눗셈을 덧셈·뺄셈보다 먼저 계산!'
  },
  {
    id: 'N16', number: 16, category: '혼합 계산', difficulty: 'hard',
    question: '(-2)² + (-3) × (-4) ÷ 6의 값은?',
    choices: ['+6', '+8', '+2', '-6'],
    answer: 0,
    explanation: [
      '(-2)² = (-2) × (-2) = +4',
      '(-3) × (-4) = +12',
      '+12 ÷ 6 = +2',
      '4 + 2 = +6',
    ],
    concept: '혼합 계산 심화', learnStepId: 'NT07',
  },
  {
    id: 'N17', number: 17, category: '분배법칙', difficulty: 'hard',
    question: '(-5) × (+3) + (-5) × (-7)을 분배법칙으로 계산하면?',
    choices: ['+20', '-20', '+10', '-10'],
    answer: 0,
    explanation: [
      '분배법칙: a×b + a×c = a×(b+c)',
      '(-5)×(+3) + (-5)×(-7) = (-5) × {(+3) + (-7)}',
      '= (-5) × (-4)',
      '= +20 (같은 부호 → 양수)',
    ],
    concept: '분배법칙', learnStepId: 'NT07',
  },
  {
    id: 'N18', number: 18, category: '유리수 덧셈', difficulty: 'medium',
    question: '-1/3 + 1/2의 값은?',
    choices: ['1/6', '-1/6', '1/5', '-1/5'],
    answer: 0,
    explanation: [
      '통분: 분모 3과 2의 최소공배수 = 6',
      '-1/3 = -2/6, +1/2 = +3/6',
      '(-2/6) + (+3/6): 절댓값 차 = 1/6, +3/6이 크므로 양수',
      '→ +1/6',
    ],
    concept: '유리수 덧셈', learnStepId: 'NT04',
  },
  {
    id: 'N19', number: 19, category: '활용', difficulty: 'hard',
    question: '어떤 수에서 (-3)을 뺐더니 +7이 되었다. 어떤 수는?',
    choices: ['+4', '+10', '-10', '-4'],
    answer: 0,
    explanation: [
      '어떤 수를 □라 하면: □ - (-3) = +7',
      '□ + 3 = 7 (뺄셈 → 덧셈 변환)',
      '□ = 7 - 3 = +4',
      '검산: (+4) - (-3) = (+4) + (+3) = +7 ✓',
    ],
    concept: '방정식 활용', learnStepId: 'NT04',
  },
  {
    id: 'N20', number: 20, category: '심화', difficulty: 'hard',
    question: 'a = -2, b = 3일 때, a² - 2ab + b²의 값은?',
    choices: ['25', '1', '-1', '13'],
    answer: 0,
    explanation: [
      'a = -2, b = 3 대입',
      'a² = (-2)² = 4',
      '2ab = 2 × (-2) × 3 = -12',
      'b² = 3² = 9',
      'a² - 2ab + b² = 4 - (-12) + 9 = 4 + 12 + 9 = 25',
      '참고: (a-b)² = (-2-3)² = (-5)² = 25 ← 빠른 계산법!',
    ],
    concept: '식의 값 계산', learnStepId: 'NT05',
  },
]

interface Props { onGoLearn?: (stepId: string) => void }

export default function N2TestPage({ onGoLearn }: Props) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showResult, setShowResult] = useState(false)
  const [timeLeft, setTimeLeft] = useState(25 * 60)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current!); handleSubmit(); return 0 }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const handleSubmit = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: 25 * 60 - timeLeft,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS
        .filter((q, i) => answers[i] !== q.answer)
        .map(q => q.concept),
      grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F',
      passed: score >= PASS_SCORE,
    })
    setPhase('result')
    setShowResult(false)
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">↔️</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">정수와 유리수 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">N1-2 단원 전 범위 · 20문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-blue-500" />, label: '문항 수', value: '20문항' },
              { icon: <Clock size={18} className="text-orange-500" />, label: '제한 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-blue-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {['NT01 양수·음수·정수', 'NT02 수직선·절댓값', 'NT03 정수 대소 비교', 'NT04 덧셈·뺄셈', 'NT05 곱셈·나눗셈', 'NT06 유리수 분류', 'NT07 혼합 계산', '분배법칙·활용 문제'].map(t => (
                <div key={t} className="flex items-center gap-1"><span className="text-blue-400">•</span> {t}</div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]
    const urgent = timeLeft < 5 * 60

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' : q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm ${urgent ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-slate-100 text-slate-700'}`}>
            <Clock size={14} /> {fmt(timeLeft)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5">{q.number}. {q.question}</p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${answered === i ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'}`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-bold text-sm hover:bg-blue-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${i === current ? 'bg-blue-500 text-white' : answers[i] !== null ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answers.filter(a => a !== null).length}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 정수·유리수 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '합격선', value: `${PASS_SCORE}점`, color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => { setPhase('intro'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setTimeLeft(25 * 60); setShowResult(false); setExpandedExpl(new Set()) }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-violet-600" />
            <span className="text-sm font-black text-violet-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowResult(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showResult ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showResult && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">{q.misconceptionCode}</span>}
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${i === q.answer ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300' : i === myAns ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-500'}`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => { const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n })}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>)}
                        </div>
                      )}
                      {onGoLearn && q.learnStepId && (
                        <button onClick={() => onGoLearn(q.learnStepId!)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-600 flex items-center gap-1">
                          <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct ? <CheckCircle size={14} className="text-emerald-500 mx-auto" /> : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal conceptTag={videoModal.stepId} conceptLabel={videoModal.label} viewerId="student_001" onClose={() => setVideoModal(null)} onCompleted={() => setVideoModal(null)} />
      )}
    </div>
  )
}
